import { api_configuration } from "../../common";
import hash = require("../widgets/dom/hash");
import api_settings_decorator = api_configuration.libraries.gui_definition_lib.types.settings.alan_api;
import { DatastoreConnection, InvalidTokenError } from "../interface/DatastoreProxy";
import { getFileFromWebServer } from "./webserver";

export type AuthenticationData = {
	token: string;
	username: string;
};
export async function authenticate(
	application_name: string,
	connection: DatastoreConnection
): Promise<AuthenticationData | undefined> {
	const authentication_data =
		readTokenFromHash() ?? (await getKauth(connection)) ?? (await readTokenFromSession(connection));
	if (authentication_data !== undefined) {
		return authentication_data;
	}
	const gui_settings: api_settings_decorator.Tsettings = await getFileFromWebServer(
		"gui_definition/package/settings.alan.json",
		JSON.parse
	);
	const state = gui_settings.settings["allow anonymous user"] as Extract<
		typeof gui_settings.settings["allow anonymous user"],
		Array<any>
	>;
	switch (state[0]) {
		case "yes":
			return undefined;
		case "no":
			return login(application_name, connection);
		default:
			return unexpectedState(state[0]);
	}
}

const AUTH_USER_KEY = "auth-user";
const AUTH_TOKEN_KEY = "auth-token";
function readTokenFromHash(): AuthenticationData | undefined {
	const token = hash.getToken();
	const username = hash.getUserName();
	hash.clearToken();
	hash.clearUsername();
	if (token !== null && username !== null) {
		window.sessionStorage.setItem(AUTH_USER_KEY, username);
		window.sessionStorage.setItem(AUTH_TOKEN_KEY, token);
		return {
			username: username,
			token: token,
		};
	} else {
		return undefined;
	}
}

async function readTokenFromSession(connection: DatastoreConnection): Promise<AuthenticationData | undefined> {
	const token = window.sessionStorage.getItem(AUTH_TOKEN_KEY);
	const username = window.sessionStorage.getItem(AUTH_USER_KEY);
	if (token !== null && username !== null) {
		const token_is_valid = await connection.validateToken(token);
		if (token_is_valid) {
			return {
				username: username,
				token: token,
			};
		}
		return undefined;
	}
	return undefined;
}

async function getKauth(connection: DatastoreConnection): Promise<AuthenticationData | undefined> {
	const kauth_text = window.localStorage.getItem("KAUTH");
	if (kauth_text !== null) {
		let kauth:
			| {
					"has session":
						| ["no", any]
						| [
								"yes",
								{
									token: string;
									uname: string;
								}
						  ];
			  }
			| undefined;
		try {
			kauth = JSON.parse(kauth_text);
		} catch (e) {
			return Promise.resolve(undefined);
		}
		if (kauth !== undefined && kauth["has session"]?.[0] === "yes") {
			const username = kauth["has session"][1].uname;
			const kauth_token = kauth["has session"][1].token;
			const token_is_valid = await connection.validateToken(kauth_token);
			if (token_is_valid) {
				return {
					username: username,
					token: kauth_token,
				};
			}
			return undefined;
		} else {
			return Promise.resolve(undefined);
		}
	}
	return Promise.resolve(undefined);
}

export async function login(application_name: string, connection: DatastoreConnection): Promise<undefined> {
	try {
		const uri = await connection.getAuthenticationUri();
		const params = new URLSearchParams({
			source: redirectURL(),
			action: "login",
			symnam: application_name,
		});
		const url = new URL(uri);
		url.hash = params.toString();
		window.location.href = url.toString();
		return undefined;
	} catch (e) {
		if (e instanceof InvalidTokenError) {
			window.localStorage.removeItem("KAUTH");
		}
		return undefined;
	}
}

export async function logout(application_name: string, connection: DatastoreConnection): Promise<void> {
	const uri = await connection.getAuthenticationUri();
	const params = new URLSearchParams({
		source: redirectURL(),
		action: "logout",
		symnam: application_name,
	});
	const url = new URL(uri);
	url.hash = params.toString();
	window.location.href = url.toString();
}

function redirectURL() {
	const url = new URL(window.location.href);
	const serialize_params = btoa(url.hash.slice(1));
	const params = new URLSearchParams({
		parameters: serialize_params,
	});
	url.hash = params.toString();
	return url.toString();
}
